import React, { useState, useEffect } from 'react';
import { Spin, Alert, theme, Card, Row, Col, Empty, Typography, Tag, Space, Badge } from 'antd';
import { useNavigate } from 'react-router-dom';
import { TrophyOutlined, GiftOutlined, ClockCircleOutlined, TeamOutlined } from '@ant-design/icons';
import MenuLayout from '../Layouts/MenuLayout';
import useAxiosInstance from '../Hooks/axiosInstance';

const { Title, Text } = Typography;

interface Item {
    uuid: number;
    titolo: string;
}

// Helper function to extract error message from backend
const getErrorMessage = (error: any): string => {
    if (error?.response?.data?.msg) {
        return error.response.data.msg;
    }
    if (error?.response?.data?.message) {
        return error.response.data.message;
    }
    if (error?.message) {
        return error.message;
    }
    return String(error);
};

const Sorteggi: React.FC = () => {
    const [items, setItems] = useState<Item[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    const navigate = useNavigate();
    const axiosInstance = useAxiosInstance();

    const {
        token: { colorBgContainer },
    } = theme.useToken();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setLoading(true);
        axiosInstance.get('/games').then((response) => {
                setItems(response.data || []);
                setError(null);
            })
            .catch((error) => {
                setError('Errore nel caricamento dei dati: ' + getErrorMessage(error));
                console.error('Error fetching data:', error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [apiEndpoint]);

    const handleCardClick = (uuid: number) => {
        navigate(`/sorteggio/${uuid}`);
    };

    // Color palette for cards
    const cardColors = [
        { bg: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)', icon: '#fff' },
        { bg: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', icon: '#fff' },
        { bg: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)', icon: '#fff' },
        { bg: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)', icon: '#fff' },
        { bg: 'linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)', icon: '#fff' },
    ];

    return (
        <MenuLayout pageTitle="I tuoi Sorteggi">
            <div style={{
                padding: isMobile ? '12px' : '24px',
                background: '#ffffff',
                minHeight: '100vh'
            }}>
                {/* Header Card */}
                <Card
                    style={{
                        marginBottom: '24px',
                        background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                        border: 'none',
                        borderRadius: '16px'
                    }}
                    bodyStyle={{ padding: isMobile ? '20px' : '32px', textAlign: 'center' }}
                >
                    <GiftOutlined style={{ fontSize: isMobile ? '40px' : '56px', color: 'white', marginBottom: '12px' }} />
                    <Title level={isMobile ? 4 : 2} style={{ color: 'white', marginBottom: '8px', marginTop: 0 }}>
                        I Miei Secret Santa
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '14px' : '16px' }}>
                        Gestisci tutti i tuoi sorteggi natalizi
                    </Text>
                </Card>

                {error && (
                    <Alert
                        message="Errore"
                        description={error}
                        type="error"
                        showIcon
                        style={{
                            marginBottom: '24px',
                            borderRadius: '12px',
                            fontSize: isMobile ? '14px' : '16px'
                        }}
                    />
                )}

                <Spin spinning={loading} size="large" tip="Caricamento dei sorteggi...">
                    {!loading && items.length === 0 ? (
                        <Card
                            style={{
                                borderRadius: '16px',
                                textAlign: 'center',
                                padding: isMobile ? '32px 16px' : '64px 32px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }}
                        >
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <div>
                                        <Title level={4} style={{ color: '#999', marginTop: '16px' }}>
                                            Nessun sorteggio trovato
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: '15px' }}>
                                            Crea il tuo primo Secret Santa per iniziare!
                                        </Text>
                                    </div>
                                }
                            />
                        </Card>
                    ) : (
                        <div>
                            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <TrophyOutlined style={{ fontSize: '20px', color: '#d32f2f' }} />
                                <Text strong style={{ fontSize: '16px', color: '#666' }}>
                                    Trovati {items.length} sorteggi
                                </Text>
                            </div>
                            <Row gutter={[16, 16]}>
                                {items.map((item, index) => {
                                    const colorScheme = cardColors[index % cardColors.length];
                                    return (
                                        <Col key={item.uuid} xs={24} sm={12} md={8} lg={6}>
                                            <Card
                                                hoverable
                                                onClick={() => handleCardClick(item.uuid)}
                                                className="sorteggio-card"
                                                style={{
                                                    borderRadius: '16px',
                                                    overflow: 'hidden',
                                                    border: 'none',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    cursor: 'pointer',
                                                    height: '100%'
                                                }}
                                                bodyStyle={{ padding: 0 }}
                                            >
                                                {/* Card Header with Gradient */}
                                                <div style={{
                                                    background: colorScheme.bg,
                                                    padding: isMobile ? '20px 16px' : '24px 20px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '12px'
                                                }}>
                                                    <div style={{
                                                        width: '56px',
                                                        height: '56px',
                                                        borderRadius: '50%',
                                                        background: 'rgba(255,255,255,0.2)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backdropFilter: 'blur(10px)'
                                                    }}>
                                                        <GiftOutlined style={{ fontSize: '28px', color: colorScheme.icon }} />
                                                    </div>
                                                    <Badge count={`#${index + 1}`} style={{
                                                        backgroundColor: 'rgba(255,255,255,0.3)',
                                                        color: 'white',
                                                        fontWeight: 600
                                                    }} />
                                                </div>

                                                {/* Card Content */}
                                                <div style={{ padding: isMobile ? '16px' : '20px' }}>
                                                    <Title
                                                        level={5}
                                                        style={{
                                                            marginTop: 0,
                                                            marginBottom: '12px',
                                                            fontSize: isMobile ? '15px' : '16px',
                                                            fontWeight: 600,
                                                            color: '#333',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                        title={item.titolo}
                                                    >
                                                        {item.titolo}
                                                    </Title>

                                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <ClockCircleOutlined style={{ color: '#999', fontSize: '14px' }} />
                                                            {/*<Text type="secondary" style={{ fontSize: '13px' }}>*/}
                                                            {/*    ID: {item.uuid}*/}
                                                            {/*</Text>*/}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <TeamOutlined style={{ color: '#2e7d32', fontSize: '14px' }} />
                                                            <Tag color="success" style={{ fontSize: '12px' }}>
                                                                Attivo
                                                            </Tag>
                                                        </div>
                                                    </Space>
                                                </div>

                                                {/* Card Footer */}
                                                <div style={{
                                                    borderTop: '1px solid #f0f0f0',
                                                    padding: isMobile ? '12px 16px' : '14px 20px',
                                                    background: '#fafafa'
                                                }}>
                                                    <Text
                                                        strong
                                                        style={{
                                                            color: '#d32f2f',
                                                            fontSize: '13px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        Clicca per aprire →
                                                    </Text>
                                                </div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    )}
                </Spin>
            </div>
        </MenuLayout>
    );
};

export default Sorteggi;
